import type { IntlShape } from 'react-intl';

import { ClientSyncType } from '~/enums/ClientSyncType';
import { FeatureFlagType } from '~/enums/FeatureFlagType';
import { ClientAction, Configs } from '~/features/admin/types';
import { sortByStringValue } from '~/utils/array';

import { getAdminConstants } from './constants';

export const getFeatureFlags = (configs: Configs) => {
  const currentFlagValues = [];

  const isEvifs = configs.syncType === ClientSyncType.Evifs;

  if (configs.bulkDocumentMetadataEnabled) {
    currentFlagValues.push(FeatureFlagType.BulkDocumentMetadata);

    if (configs.bulkDocumentMetadataFilenameUpdateEnabled) {
      currentFlagValues.push(
        FeatureFlagType.BulkDocumentMetadataFilenameUpdate,
      );
    }
    if (configs.bulkDocumentMetadataMultipleTasksEnabled) {
      currentFlagValues.push(FeatureFlagType.BulkDocumentMetadataMultipleTasks);

      if (configs.bulkDocumentMetadataFileMoveEnabled) {
        currentFlagValues.push(FeatureFlagType.BulkDocumentMetadataFileMove);
      }
    }
  }
  if (configs.documentGroupRevampEnabled) {
    currentFlagValues.push(FeatureFlagType.DocumentGroupRevamp);
  }
  if (configs.inboundEmailEnabled) {
    currentFlagValues.push(FeatureFlagType.InboundEmail);
  }
  if (configs.exportExcelEnabled) {
    currentFlagValues.push(FeatureFlagType.ExportExcel);
  }
  if (configs.externalApiEnabled) {
    currentFlagValues.push(FeatureFlagType.ExternalApi);
  }
  if (configs.automationSmartClauseFullEnabled) {
    currentFlagValues.push(FeatureFlagType.AutomationSmartClauseFull);
  }
  if (configs.automationSmartClauseApiEnabled) {
    currentFlagValues.push(FeatureFlagType.AutomationSmartClauseApi);
  }
  if (configs.workflowEnabled) {
    currentFlagValues.push(FeatureFlagType.Workflow);
  }
  if (configs.searchV2Enabled) {
    currentFlagValues.push(FeatureFlagType.SearchV2);
  }
  if (configs.ssoEnabled) {
    currentFlagValues.push(FeatureFlagType.SingleSignOn);
  }
  if (isEvifs && configs.syncPairWebhookEnabled) {
    currentFlagValues.push(FeatureFlagType.SyncPairWebhook);
  }
  if (configs.allowClauseRenamingEnabled) {
    currentFlagValues.push(FeatureFlagType.AllowClauseRenaming);
  }
  if (configs.esWeightedSearchEnabled) {
    currentFlagValues.push(FeatureFlagType.EsWeightedSearch);
  }
  return currentFlagValues?.sort() ?? [];
};

export const getFeatureFlagOptions = (configs: Configs, intl: IntlShape) => {
  const { FEATURE_FLAG_OPTIONS, bulkDocumentMetadataMultipleTasksValue } =
    getAdminConstants(intl);
  const base = [
    ...FEATURE_FLAG_OPTIONS,
    {
      value: FeatureFlagType.AutomationSmartClauseFull,
      label: intl.formatMessage({
        id: 'AdminUtils.automationSmartClauseFullLabel',
        defaultMessage: 'Automation SmartClause Full',
        description: 'Feature flag option for automation smart clause full',
      }),
    },
    {
      value: FeatureFlagType.AutomationSmartClauseApi,
      label: intl.formatMessage({
        id: 'AdminUtils.automationSmartClauseApiLabel',
        defaultMessage: 'Automation SmartClause API',
        description: 'Feature flag option for automation smart clause API',
      }),
    },
  ];

  if (configs.bulkDocumentMetadataEnabled) {
    base.push(
      {
        value: FeatureFlagType.BulkDocumentMetadataFilenameUpdate as const,
        label: intl.formatMessage({
          id: 'AdminUtils.bulkMetadataRenameFilesLabel',
          defaultMessage: 'Bulk Metadata Updater: Rename Files',
          description: 'Feature flag option for bulk metadata rename files',
        }),
      },
      bulkDocumentMetadataMultipleTasksValue,
    );

    if (configs.bulkDocumentMetadataMultipleTasksEnabled) {
      base.push({
        value: FeatureFlagType.BulkDocumentMetadataFileMove,
        label: intl.formatMessage({
          id: 'AdminUtils.bulkMetadataMoveFilesLabel',
          defaultMessage: 'Bulk Metadata Updater: Move Files',
          description: 'Feature flag option for bulk metadata move files',
        }),
      });
    }
  }

  return base.sort(sortByStringValue('label'));
};

export const getTitle = (action: ClientAction, intl: IntlShape) => {
  switch (action) {
    case 'add':
      return intl.formatMessage({
        id: 'ClientDetails.addClientTitle',
        defaultMessage: 'Add New Client',
        description: 'Title for adding a new client',
      });
    case 'edit':
      return intl.formatMessage({
        id: 'ClientDetails.editClientTitle',
        defaultMessage: 'Edit Client Information',
        description: 'Title for editing client information',
      });
    case 'delete':
      return intl.formatMessage({
        id: 'ClientDetails.deleteClientTitle',
        defaultMessage: 'Delete Client Information',
        description: 'Title for deleting a client',
      });
    case 'viewCredits':
      return intl.formatMessage({
        id: 'ClientDetails.viewCreditsTitle',
        defaultMessage: 'View Credits Client Information',
        description: 'Title for viewing client credits',
      });
    case 'checkBucket':
      return intl.formatMessage({
        id: 'ClientDetails.checkBucketTitle',
        defaultMessage: 'Check Bucket Client Information',
        description: 'Title for checking bucket',
      });
    default:
      return intl.formatMessage({
        id: 'ClientDetails.defaultTitle',
        defaultMessage: 'Client Information',
        description: 'Title for client information',
      });
  }
};
export const getAction = (action: ClientAction, intl: IntlShape) => {
  switch (action) {
    case 'delete':
      return intl.formatMessage({
        id: 'ClientModal.deleteAction',
        defaultMessage: 'delete',
        description: 'Action for deleting a client',
      });
  }
};
